import { Injectable } from '@angular/core';
import { VehicleService } from 'src/app/vehicles/services/vehicle.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { DriversService } from 'src/app/drivers/services/drivers.service';
import { MomentService } from 'src/app/vehicles/services/moment.service';
import { take, filter } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { SafeUrl } from '@angular/platform-browser';
import { ReportPayload } from 'src/app/reports/models/report-payload';
import { DomSanitizer } from '@angular/platform-browser';
import { setExcelDateTimeFormat } from 'src/app/reports/helpers/setExcelDateTimeFormat';

export interface Checklist {
  clase: string,
  uuid: string,
  data?: ChecklistData
}

interface ChecklistData {
  num_plate: string;
  driver: string
  code_driver: string;
  is_checked: boolean;
  date_check: string;
}
interface ChecklistResponse {
  success: boolean;
  message: string;
  data?: any[];
}


interface ImageCacheItem {
  blob: Blob | null;
  objectUrl: string;
  safeUrl: SafeUrl | string;
  isLoading: boolean;
  hasError: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ChecklistEventService {

  private backwardToleranceMs = 20 * 60 * 1000; // 20 minutos
  private forwardToleranceMs = 1 * 60 * 1000; // 1 minutos

  private checklistEventsSubject = new BehaviorSubject<{ [uuid: string]: Checklist }>({});
  checklistEvents$ = this.checklistEventsSubject.asObservable();

  private checklistHistory = new Map<string, ChecklistData[]>(); // fecha : checklsit data
  private loadingDates = new Map<string, Promise<ChecklistData[]>>();

  private checklistQueue: Promise<void> = Promise.resolve();

  public checkListClasses = {
    COMPLETED: 'checklist-completed',
    NO_COMPLETED: 'checklist-no-completed',
    DANGER: 'checklist-danger',
    WAITING: 'checklist-waiting'
  }
  private vehicleDataReadyPromise: Promise<void> | null = null;
  private resolveVehicleDataReady: (() => void) | null = null;
  constructor(
    private vehicleService: VehicleService,
    private localStorageService: LocalStorageService,
    private http: HttpClient,
    private driverService: DriversService,
    private momentService: MomentService,
    private spinner: NgxSpinnerService,
    private sanitizer: DomSanitizer,
  ) {

    const checklistsStorage = this.getAllChecklistsFromLocalStorage();
    this.checklistEventsSubject.next(checklistsStorage)

    this.vehicleDataReadyPromise = new Promise<void>((resolve) => {
      this.resolveVehicleDataReady = resolve;
    });

    this.vehicleService.dataCompleted.subscribe(() => {
      if (this.resolveVehicleDataReady) {
        this.resolveVehicleDataReady();
        this.resolveVehicleDataReady = null; // evitar reuso
      }
    });
  }


  async getChecklistForDate(date: string): Promise<ChecklistData[]> {
    const formattedDate = date;

    if (this.checklistHistory.has(formattedDate)) {
      return this.checklistHistory.get(formattedDate)!;
    }

    if (this.loadingDates.has(formattedDate)) {
      return this.loadingDates.get(formattedDate)!;
    }

    const loadPromise = this.getAllChecklistsByDate(formattedDate).toPromise()
      .then(response => {
        const checklistData = this.formatChecklistData(response.data || [])
        console.log("checklist : load checklist histoty to ", formattedDate)
        this.checklistHistory.set(formattedDate, checklistData);
        this.loadingDates.delete(formattedDate);
        console.log("checklist : history load completed", this.checklistHistory)
        return checklistData;
      })
      .catch(err => {
        this.loadingDates.delete(formattedDate);
        throw err;
      });

    this.loadingDates.set(formattedDate, loadPromise);
    return loadPromise;
  }
  private formatChecklistData(data: any[]) {
    let dataFormat: ChecklistData[] = []
    for (const item of data) {
      const codigoDriver = item.COD_TRAB;
      const driverName = codigoDriver
        ? this.driverService.getDriverByDni(codigoDriver)
        : "Conductor No Encontrado";

      const fechaFormateada = item.FECHA
        .replace(/-/g, '/')
        .replace('T', ' ')
        .slice(0, 19); // Resultado: '2025/07/28 00:21:07'

      const fechaItemLocal = this.momentService.toLocal(fechaFormateada, true);

      dataFormat.push({
        num_plate: item.PLACA,
        driver: driverName,
        code_driver: codigoDriver,
        is_checked: item.TIENECHECKLIST === "SI",
        date_check: fechaItemLocal.toString(),
      })
    }
    return dataFormat
  }
  private formatDateToString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Mes comienza en 0
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}${month}${day}`;
  }
  // load multple events (event-service)
  public async loadChecklistEventsHistory(events: any[]): Promise<Record<string, Checklist>> {
    const result: Record<string, Checklist> = {};
    for (const event of events) {
      if (event.evento === "checklist-daily") {
        const checklist = await this.loadChecklistEventByHistory(event);
        if (checklist) {
          result[event.uuid_event] = checklist;
        }
      }
    }
    console.log("checklist : resultado final checklist objet ", result)
    const currentData = this.checklistEventsSubject.getValue();
    const updatedData = { ...currentData, ...result };
    this.checklistEventsSubject.next(updatedData);
    return result;
  }

  public async loadChecklistEventByHistory(event: any): Promise<Checklist | undefined> {

    const checklistCached = this.getChecklistFromLocalStorage(event.uuid_event);
    if (checklistCached) {
      return checklistCached;
    }
    const fecha = event.fecha_tracker;
    const soloFecha = fecha.split(" ")[0].replace(/\//g, '');

    // Esperar que los datos de vehículo estén listos
    const waitVehicleReady = !this.vehicleService.statusDataVehicle && this.vehicleDataReadyPromise
      ? this.vehicleDataReadyPromise
      : Promise.resolve();

    try {
      await waitVehicleReady;
      const history = await this.getChecklistForDate(soloFecha)
      let historyByPlate = this.groupChecklistsByPlate(history)
      const checklist = await this.addEventChecklistFromHistory(event, historyByPlate);

      // Después de construirlo, actualizar el subject aquí (si así lo deseas)
      return checklist;
    } catch (err) {
      console.error("checklist : Error al cargar evento desde historial", err);
      return undefined;
    }
  }

  public groupChecklistsByPlate(data: ChecklistData[]): Record<string, ChecklistData[]> {
    return data.reduce((acc, item) => {
      const plate = item.num_plate;
      if (!acc[plate]) {
        acc[plate] = [];
      }
      acc[plate].push(item);
      return acc;
    }, {} as Record<string, ChecklistData[]>);
  }

  private async addEventChecklistFromHistory(event: any, history: Record<string, ChecklistData[]>, includePreviousDate: boolean = true): Promise<Checklist> {
    // console.log("checklist : add new checklist event from history", event)
    const imei = event["imei"]
    const checklistCached = this.getChecklistFromLocalStorage(event.uuid_event)
    if (checklistCached) {
      return checklistCached;
    } else {
      let checklist: Checklist = {
        clase: this.checkListClasses.WAITING,
        uuid: event.uuid_event
      }

      const vehicle = this.vehicleService.getVehicle(imei)

      if (!vehicle || !vehicle.plate_number) {
        checklist.clase = this.checkListClasses.NO_COMPLETED
      } else {
        let plate_number = vehicle.plate_number || ""
        if (history[plate_number]) {
          const fecha_event = new Date(event.fecha_tracker.replace(/\//g, '-'));

          let itemMasCercano = this.findClosestChecklistItem(history[plate_number], plate_number, fecha_event);

          // Resultado final
          if (!itemMasCercano) {

            const fechaLimite = new Date(fecha_event.getTime() - this.backwardToleranceMs);
            // Si la fecha límite está en el día anterior, entonces sí buscar en día anterior
            if (fechaLimite.getDate() < fecha_event.getDate() && includePreviousDate) {
              // // Calcular fecha anterior y formatear
              const fechaAnterior = new Date(fecha_event);
              fechaAnterior.setDate(fecha_event.getDate() - 1);

              const soloFechaAnterior = this.formatDateToString(fechaAnterior);

              // // Pedir historial del día anterior
              const historyAnterior = await this.getChecklistForDate(soloFechaAnterior);
              const historyByPlate = this.groupChecklistsByPlate(historyAnterior)
              // // Llamar función para buscar checklist en historial del día anterior
              const checklistAnterior = await this.addEventChecklistFromHistory(event, historyByPlate, false);
              // console.log("checklist : find yesterday history", new Date(fecha_event), soloFechaAnterior, checklistAnterior)
              return checklistAnterior
              // checklist.clase = this.checkListClasses.NO_COMPLETED
            } else {
              checklist.clase = this.checkListClasses.NO_COMPLETED
            }

          } else {
            checklist.clase = this.checkListClasses.COMPLETED;
            checklist.data = itemMasCercano;
          }
        } else {
          checklist.clase = this.checkListClasses.NO_COMPLETED
        }
      }

      // console.log("checklist : checklist finaly", checklist)
      const clasesValidas = [this.checkListClasses.COMPLETED, this.checkListClasses.NO_COMPLETED];
      if (clasesValidas.includes(checklist.clase)) {
        // console.log("checklist : saved in the localStorage", checklist)
        this.setChecklistToLocalStorage(event.uuid_event, checklist);
      }
      return checklist;
    }
  }


  private findClosestChecklistItem(
    history: ChecklistData[],
    plate_number: string,
    fecha_event: Date
  ): ChecklistData | null {
    if (history.length <= 0) {
      return null;
    }

    let itemMasCercano: ChecklistData | null = null;
    let menorDiferencia = Number.MAX_SAFE_INTEGER; // grande para empezar

    for (const item of history) {
      if (item.num_plate !== plate_number) continue;

      const fechaItem = new Date(item.date_check);
      const diferencia = fechaItem.getTime() - fecha_event.getTime(); // positivo si está adelante, negativo si atrás
      const diferenciaAbs = Math.abs(diferencia);

      // Condición: está dentro de alguna de las tolerancias
      if (
        (diferencia <= 0 && Math.abs(diferencia) <= this.backwardToleranceMs) || // dentro de los 20 min atrás
        (diferencia > 0 && diferencia <= this.forwardToleranceMs)               // dentro de los 10 min adelante
      ) {
        if (diferenciaAbs < menorDiferencia) {
          menorDiferencia = diferenciaAbs;
          itemMasCercano = item;
        }
      }
    }

    return itemMasCercano;
  }

  // load checklist by event (event-service <- event-socket-service)
  public loadChecklistEvent(event: any) {
    console.log("checklist: listen add new event checklist in the queue (waiting vehicle)", event)
    this.checklistQueue = this.checklistQueue.then(() => {
      return this.processEvent(event);
    }).catch(err => {
      console.error('checklist : Error en la cola de checklist:', err);
    });
  }
  private async processEvent(event: any): Promise<void> {
    if (!this.vehicleService.statusDataVehicle && this.vehicleDataReadyPromise) {
      await this.vehicleDataReadyPromise;
    }
    await this.addEventChecklist(event);
  }
  private async addEventChecklist(event: any) {
    console.log("checklist : add new event checklist", event)
    const checklistCached = this.getChecklistFromLocalStorage(event.uuid_event)
    if (checklistCached) {
      const currentData = this.checklistEventsSubject.getValue();
      console.log("checklist : finaly checklist from localstorage", event)
      const updatedData = { ...currentData, [event.uuid_event]: checklistCached };
      this.checklistEventsSubject.next(updatedData);
    } else {
      let checklist: Checklist = {
        clase: this.checkListClasses.WAITING,
        uuid: event.uuid_event
      }
      const vehicle = this.vehicleService.getVehicle(event['imei'])
      if (!vehicle) {
        console.log("checklist : vehicle no fund in the checklilist event", event)
        checklist.clase = this.checkListClasses.NO_COMPLETED
      } else {
        const response = await this.getChecklistByVehicle(vehicle.plate_number, event.fecha_tracker).toPromise()
        console.log("checklist : get response from request checklist", response)
        checklist = await this.findClosestChecklistWithFallback(response, checklist, event, vehicle.plate_number)
      }
      console.log("checklist : finaly checklist", checklist)
      const currentData = this.checklistEventsSubject.getValue();
      const updatedData = { ...currentData, [event.uuid_event]: checklist };
      const clasesValidas = [this.checkListClasses.COMPLETED, this.checkListClasses.NO_COMPLETED];
      if (clasesValidas.includes(checklist.clase)) {
        console.log("checklist : saved finaly checklist in the localstorage", checklist)
        this.setChecklistToLocalStorage(event.uuid_event, checklist);
      }
      this.checklistEventsSubject.next(updatedData);
    }
  }

private async findClosestChecklistWithFallback(
  response: ChecklistResponse,
  checklist: Checklist,
  event: any,
  plate: string,
  alreadyTriedPreviousDate = false
): Promise<Checklist> {
  if (!response.success || !response.data) {
    checklist.clase = this.checkListClasses.DANGER;
    return checklist;
  }

  const data = response.data;
  const fechaEvento = new Date(event.fecha_tracker.replace(/\//g, '-'));
  let itemMasCercano: any = null;
  let menorDiferencia = Number.MAX_SAFE_INTEGER;

  // Tolerancias
  const toleranciaAtrasMs = this.backwardToleranceMs;
  const toleranciaAdelanteMs = this.forwardToleranceMs;

  for (const item of data) {
    const fechaItem = new Date(this.momentService.toLocal(item.FECHA, true).toString());
    const diffMs = fechaItem.getTime() - fechaEvento.getTime();

    // Condición: dentro de las tolerancias
    if (
      // Antes del evento (hasta 20 min)
      (diffMs <= 0 && Math.abs(diffMs) <= toleranciaAtrasMs) ||
      // Después del evento (hasta 10 min)
      (diffMs > 0 && diffMs <= toleranciaAdelanteMs)
    ) {
      if (Math.abs(diffMs) < menorDiferencia) {
        menorDiferencia = Math.abs(diffMs);
        itemMasCercano = item;
      }
    }
  }

  if (!itemMasCercano) {
    // Si no encontramos nada, probamos una vez con el día anterior
    if (!alreadyTriedPreviousDate) {
      const fechaLimite = new Date(fechaEvento.getTime() - toleranciaAtrasMs);
      const mismoDia = fechaLimite.toDateString() === fechaEvento.toDateString();

      if (!mismoDia) {
        const fechaAnterior = new Date(fechaEvento);
        fechaAnterior.setDate(fechaEvento.getDate() - 1);

        const fechaAnteriorStr = this.formatDateToString(fechaAnterior);
        const responseAnterior = await this.getChecklistByVehicle(plate, fechaAnteriorStr).toPromise();

        return await this.findClosestChecklistWithFallback(responseAnterior, checklist, event, plate, true);
      }
    }

    // Nada encontrado ni en el día anterior
    checklist.clase = this.checkListClasses.NO_COMPLETED;
  } else {
    const codigoDriver = itemMasCercano.COD_TRAB;
    const driverName = codigoDriver
      ? this.driverService.getDriverByDni(codigoDriver)
      : "Conductor No Encontrado";

    checklist.clase = this.checkListClasses.COMPLETED;
    checklist.data = {
      num_plate: plate,
      driver: driverName,
      code_driver: codigoDriver,
      is_checked: itemMasCercano.TIENECHECKLIST === "SI",
      date_check: fechaEvento.toISOString(),
    };
  }

  return checklist;
}



  public getChecklistEvent(uuid: string): Checklist | undefined {
    return this.checklistEventsSubject.getValue()[uuid];
  }

  // api checklist
  private getChecklistByVehicle(vehicle: string, fecha_tracker: string): Observable<ChecklistResponse> {
    const vehicle_plate = vehicle.trim().slice(-6);

    if (vehicle_plate.length !== 6 || !fecha_tracker) {
      return of({ success: false, message: 'Parámetros inválidos' });
    }

    const fecha = fecha_tracker.split(' ')[0].replace(/\//g, ''); // e.g. "20250630"
    return this.http.post<ChecklistResponse>(`${environment.apiUrl}/api/checklist/getData`, {
      plate: vehicle_plate,
      date: fecha
    }).pipe(
      catchError(() => {
        return of({
          success: false,
          message: 'Error en la petición',
        });
      })
    );
  }

  private getAllChecklistsByDate(date: string): Observable<ChecklistResponse> {
    return this.http.post<ChecklistResponse>(`${environment.apiUrl}/api/checklist/getData`, {
      plate: "*",
      date: date
    }).pipe(
      catchError(() => {
        return of({
          success: false,
          message: 'Error en la petición',
        });
      })
    );
  }


  // local storage

  getAllChecklistsFromLocalStorage(): Record<string, Checklist> {
    const raw = this.localStorageService.getItem('checklists');
    if (!raw) return {};

    try {
      return JSON.parse(raw) as Record<string, Checklist>;
    } catch (error) {
      console.warn('Error parsing checklists from localStorage:', error);
      return {};
    }
  }

  getChecklistFromLocalStorage(uuid: string): Checklist | undefined {

    const checkListMemory = this.getChecklistEvent(uuid)
    if (checkListMemory) {
      return checkListMemory;
    }

    const raw = this.localStorageService.getItem('checklists');
    if (!raw) return undefined;

    const allChecklists = JSON.parse(raw) as Record<string, Checklist>;
    return allChecklists[uuid];
  }

  setChecklistToLocalStorage(uuid: string, checklist: Checklist) {
    const raw = this.localStorageService.getItem('checklists');
    const allChecklists = raw ? JSON.parse(raw) as Record<string, Checklist> : {};

    allChecklists[uuid] = checklist;
    this.localStorageService.setItem('checklists', JSON.stringify(allChecklists));
  }

  private imageChecklistLoadedSubject = new BehaviorSubject<boolean>(false);

  private progressMessageSubject = new BehaviorSubject<string>("Procesando checklist...");
  public progressMessage$ = this.progressMessageSubject.asObservable()

  imageChecklistLoaded$ = this.imageChecklistLoadedSubject.asObservable();


  private _cacheSubject = new BehaviorSubject<{ [key: string]: ImageCacheItem }>({});
  public cache$: Observable<{ [key: string]: ImageCacheItem }> = this._cacheSubject.asObservable();
  getItem(key: string): ImageCacheItem | undefined {
    return this._cacheSubject.value[key];
  }
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  async getImageBase64(fecha: string): Promise<string | null> {
    const entry = this.getItem(fecha);

    // Validar si existe y está lista
    if (!entry || entry.isLoading || entry.hasError || !entry.blob) {
      return null; // no disponible
    }

    // Convertir a base64
    const base64 = await this.blobToBase64(entry.blob);

    // devolver solo el string sin el prefijo data:image/png;base64,
    return base64.split(",")[1];
  }

  async addImageFromCache(
    workbook: ExcelJS.Workbook,
    sheet: ExcelJS.Worksheet,
    fecha: string,
    row: number,
    col: number,
    maxWidth = 50,
    maxHeight = 50
  ) {
    console.log(fecha)
    const entry = this.getItem(fecha);
    if (!entry || entry.isLoading || entry.hasError || !entry.blob) {
      console.warn(`Imagen no disponible para ${fecha}`);
      return;
    }

    // Convertir Blob a base64
    const base64 = await this.getImageBase64(fecha);
    if (!base64) return;

    // Intentar detectar extensión a partir del mimeType del blob
    const mime = entry.blob.type; // ej. "image/png"
    let extension: 'png' | 'jpeg' | 'gif' = 'png';
    if (mime.includes('jpeg') || mime.includes('jpg')) extension = 'jpeg';
    if (mime.includes('gif')) extension = 'gif';

    const imageId = workbook.addImage({ base64, extension });

    // Obtener altura actual de la fila
    const rowObj = sheet.getRow(row);
    const rowHeight = rowObj.height || 15;

    // Ajustar altura de la fila si la imagen es más alta
    const factor = 0.75; // conversión px -> puntos
    const imageHeightInPoints = maxHeight * factor;

    if (imageHeightInPoints > rowHeight) {
      rowObj.height = imageHeightInPoints;
    }

    // Escalar imagen si la fila es más pequeña que la altura deseada
    const finalHeight = rowObj.height / factor;
    const scaleFactor = finalHeight / maxHeight;

    sheet.addImage(imageId, {
      tl: { col: col - 1, row: row - 1 },
      ext: { width: maxWidth * scaleFactor, height: maxHeight * scaleFactor }
    });
  }

  async exportChecklistExcelBrowserWithImage(report : ReportPayload, color : string = "003366") {
    this.progressMessageSubject.next("Procesando checklist...")
    this.spinner.show("exportReponnerSpinner")
    if (!this.imageChecklistLoadedSubject.getValue()) {
      // esperar hasta que sea true
      await this.imageChecklistLoaded$
        .pipe(
          filter(loaded => loaded),
          take(1)
        )
        .toPromise();
    }

    this.progressMessageSubject.next("Generando Excel...")


    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Export');

    // TITULO DEL REPORTE
    const cellTitle = sheet.getCell('A1'); // solo la celda fusionada
    cellTitle.value = 'REPORTE DE CHECKLIST';
    cellTitle.font = { size: 16, bold: true, color: { argb: color } };
    cellTitle.alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.mergeCells('A1:N1');
    // Simular "padding" vertical
    sheet.getRow(1).height = 30; // ajusta el valor según necesites


    // SUBTITULO PERIODO
    sheet.addRow(['Periodo']); // Fila 2
    sheet.mergeCells('A2:N2');
    const cellPeriodo = sheet.getCell('A2'); // la celda superior izquierda de la fusión
    cellPeriodo.font = { size: 12, color: { argb: 'FFFFFFFF' } };
    cellPeriodo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'A6A6A6' } };
    cellPeriodo.alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getRow(2).height = 20; // ajusta el valor según necesites

    // MOSTRAR RANGO / PERIODO
    sheet.addRow([`${report.reportDetails.periodRange}`]); // Fila 3
    sheet.mergeCells('A3:N3');
    sheet.getRow(3).height = 20; // ajusta el valor según necesites

    const cell = sheet.getCell('A3'); // celda superior izquierda de la fusión
    cell.font = { size: 12 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EEECE1' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };


    // --- Header de tabla (fila 5 y 6) ---
    const headers = [
      { label: 'Item', span: 1 },
      { label: 'Código Interno', span: 1 },
      { label: 'Placa', span: 1 },
      { label: 'Datos del evento', span: 3, children: ['Evento', 'Fecha', 'Conductor'] },
      { label: 'Datos del checklist', span: 5, children: ['Fecha', 'Hora', 'Tiempo de diferencia', 'Código Conductor', 'Conductor'] },
      { label: 'Previsualización', span: 1 },
      { label: 'Imagen (Link)', span: 1 },
      { label: 'Video (Link)', span: 1 },
    ];

    // Generar dinámicamente las filas 5 y 6

    let colIndex = 1;
    headers.forEach(h => {
      if (h.span > 1 && h.children) {
        // Merge fila 5
        sheet.mergeCells(5, colIndex, 5, colIndex + h.span - 1);
        const parentCell = sheet.getCell(5, colIndex);
        parentCell.value = h.label;

        // Subcolumnas fila 6
        h.children.forEach((child, i) => {
          const childCell = sheet.getCell(6, colIndex + i);
          childCell.value = child;

          // 🎨 Estilo diferente solo para subcolumnas
          childCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } }; // azul claro
          childCell.font = { bold: false, color: { argb: 'FFFFFF' } };
          childCell.alignment = {
            horizontal: 'center',
            vertical: 'middle'
          };
          childCell.border = {
            top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
          };
        });

        // 🎨 Estilo diferente para el padre
        parentCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } }; // morado
        parentCell.font = { bold: true, color: { argb: 'FFFFFF' } };
        parentCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        parentCell.border = {
          top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
        };

      } else {
        // Columnas normales
        sheet.mergeCells(5, colIndex, 6, colIndex);
        const cell = sheet.getCell(5, colIndex);
        cell.value = h.label;
      }
      colIndex += h.span;
    });

    // Estilo general para todas las cabeceras fila 5 y 6
    [5, 6].forEach(rn => {
      sheet.getRow(rn).eachCell(cell => {
        if (!cell.fill) { // solo si no tiene estilo propio
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
          };
        }
      });
    });

    // --- Filas de datos ---
    for (let i = 0; i < report.dataViewer.records.length; i++) {
      const row = report.dataViewer.records[i];

      const countChecklist = row.datosChecklist?.length || 1;

      for (let j = 0; j < countChecklist; j++) {
        const checklistItem = row.datosChecklist?.[j] || {};

        let fecha = "-";
        let hora = "-";
        let tiempo = "-";
        let codigo = "-";
        let conductor = "-";

        if (checklistItem.date_checklist) {
          const partes = checklistItem.date_checklist.split(" ");
          fecha = partes[0];
          hora = partes[1] || "-";
        }
        tiempo = checklistItem.string_timing || "-";
        codigo = checklistItem.cod_driver || "-";
        conductor = checklistItem.name_driver || "-";

        const dataRow = sheet.addRow([
          i + 1,             // Item
          row.codigo,        // Código
          row.placa,         // Placa

          // detalles del evento
          row.nombreEvento,
          row.fecha,
          row.conductor,

          // detalles del checklist
          fecha,
          hora,
          tiempo,
          codigo,
          conductor,

          // previsualización
          'No se pudo cargar la imagen',

          // links
          row.linkImage && row.linkImage !== '-' ? { text: 'Imagen', hyperlink: row.linkImage } : 'Sin medios disponibles',
          row.linkVideo && row.linkVideo !== '-' ? { text: 'Video', hyperlink: row.linkVideo } : 'Sin medios disponibles',
        ]);
        // Fecha del evento
        setExcelDateTimeFormat(dataRow.getCell(5), row.fecha);

        // Fecha y hora del checklist
        setExcelDateTimeFormat(dataRow.getCell(7), fecha);
        setExcelDateTimeFormat(dataRow.getCell(8), hora);
        // Resaltar si conductor coincide
        if (conductor != "-" && conductor === row.conductor) {
          for (let col = 8; col <= 12; col++) {
            const cell = dataRow.getCell(col);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD5F5D5' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
              left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
              bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
              right: { style: 'thin', color: { argb: 'FFBFBFBF' } }
            };
          }
        }

        // Alineación general
        dataRow.eachCell(cell => {
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        });

        // Imagen para cada fila si existe
        if (row.linkImage && row.linkImage !== '-') {
          await this.addImageFromCache(workbook, sheet, row.fecha, dataRow.number, 12, 180, 180);
        }
      }
    }



    // --- Ajustar ancho de columnas ---
    const columnWidths = [
      8,   // Código
      15,  // Placa
      30,  // Evento
      22,  // Fecha evento
      25,  // Conductor evento
      20,  // Hora checklist
      25,  // Tiempo diferencia
      18,  // Código conductor checklist
      25,  // Conductor checklist
      30,  // Previsualización (imagen)
      22,  // Imagen (link descarga)
      22   // Video (link descarga)
    ];

    sheet.columns.forEach((col, i) => {
      col.width = columnWidths[i] || 15;
    });

    // --- Descargar archivo ---
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'ReporteChecklist_' + report.reportDetails.periodRange + '.xlsx');
    this.spinner.hide("exportReponnerSpinner");
  }

  async loadImageFromChecklist(report : ReportPayload , batchSize: number = 5) {
    this.imageChecklistLoadedSubject.next(false)
    const total = report.dataViewer.records.length;
    let processed = 0;
    let index = 0;

    while (index < total) {
      const batch = report.dataViewer.records.slice(index, index + batchSize);

      await Promise.all(
        batch.map((row: any) => {
          // if (row.linkImage && row.linkImage.length >= 4) {
          // }
          return this.loadImageToCache(row.linkImage, row.fecha).then(() => {
            processed++;
            this.progressMessageSubject.next(`Procesando imágenes... ${processed}/${total}`)
          });
          // processed++;
          // this.progressMessage = `Procesando imágenes... ${processed}/${total}`;
          // return Promise.resolve();
        })
      );

      index += batchSize;
    }
    this.progressMessageSubject.next(`Completado (${processed}/${total})`)
    this.imageChecklistLoadedSubject.next(true)
  }

  private updateCache(fecha: string, item: Partial<ImageCacheItem>) {
    const currentCache = this._cacheSubject.value;
    this._cacheSubject.next({
      ...currentCache,
      [fecha]: { ...currentCache[fecha], ...item }
    });
  }

  async loadImageToCache(url: string, fecha: string): Promise<void> {
    // Inicializamos como cargando
    this.updateCache(fecha, {
      blob: null,
      objectUrl: '',
      safeUrl: '',
      isLoading: true,
      hasError: false
    });

    if (!url || url.length <= 4) {
      this.updateCache(fecha, { isLoading: false, hasError: true });
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Guardar en cache como éxito
      this.updateCache(fecha, {
        blob,
        objectUrl: blobUrl,
        safeUrl: this.sanitizer.bypassSecurityTrustUrl(blobUrl),
        isLoading: false,
        hasError: false
      });

    } catch (error) {
      console.error(`Error cargando imagen de ${url}`, error);
      this.updateCache(fecha, { blob: null, objectUrl: '', safeUrl: '', isLoading: false, hasError: true });
    }
  }

}
